import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticConveyer } from "./realistic";

/**
 * ConveyerNode – Conveyor belt visualization.
 * Supports:
 * - Realistic 3D SCADA design with moving belt texture & traveling packages
 * - Glassmorphism design
 * - Classic industrial SVG layout
 */
const ConveyerNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticConveyer
              liveValue={liveValue}
              unit={unit || "RPM"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const strokeColor = isRunning ? color : "#94a3b8";
        const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const beltDuration = isRunning ? `${2 / (speedScale || 1)}s` : "0s";
        const rollerDuration = isRunning ? `${1.5 / (speedScale || 1)}s` : "0s";

        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="100"
            height="58"
            viewBox="0 0 100 58"
            fill="none"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="conveyerGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="conveyerActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                  </linearGradient>
                  <filter id="conveyerGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Belt track */}
                <rect
                  x="5"
                  y="12"
                  width="90"
                  height="20"
                  rx="10"
                  fill="url(#conveyerGlassGrad)"
                  stroke={isRunning ? "url(#conveyerActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  style={{
                    filter: isRunning ? "url(#conveyerGlow)" : "none",
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Belt surface with moving pattern (thicker lines) */}
                <g style={{ animation: isRunning ? `live-conveyer-move ${beltDuration} linear infinite` : "none" }}>
                  {[10, 25, 40, 55, 70, 85, 100].map((x) => (
                    <line key={x} x1={x} y1="13" x2={x} y2="31" stroke={color} strokeWidth="2.5" opacity="0.8" />
                  ))}
                </g>

                {/* Rollers with rotating spokes */}
                <g style={{
                  transformOrigin: "15px 22px",
                  animation: isRunning ? `live-pump-spin ${rollerDuration} linear infinite` : "none",
                }}>
                  <circle cx="15" cy="22" r="6" fill="url(#conveyerGlassGrad)" stroke={strokeColor} strokeWidth="1.5" />
                  <line x1="9" y1="22" x2="21" y2="22" stroke={strokeColor} strokeWidth="1.2" />
                  <line x1="15" y1="16" x2="15" y2="28" stroke={strokeColor} strokeWidth="1.2" />
                </g>
                <g style={{
                  transformOrigin: "85px 22px",
                  animation: isRunning ? `live-pump-spin ${rollerDuration} linear infinite` : "none",
                }}>
                  <circle cx="85" cy="22" r="6" fill="url(#conveyerGlassGrad)" stroke={strokeColor} strokeWidth="1.5" />
                  <line x1="79" y1="22" x2="91" y2="22" stroke={strokeColor} strokeWidth="1.2" />
                  <line x1="85" y1="16" x2="85" y2="28" stroke={strokeColor} strokeWidth="1.2" />
                </g>

                {/* Material on belt (larger, brighter glowing packages) */}
                {isRunning && (
                  <g style={{ animation: `live-conveyer-move ${3 / (speedScale || 1)}s linear infinite` }} filter="url(#conveyerGlow)">
                    <rect x="15" y="6" width="12" height="7" rx="1.5" fill="#facc15" stroke="#ffffff" strokeWidth="1" />
                    <rect x="50" y="6" width="12" height="7" rx="1.5" fill="#facc15" stroke="#ffffff" strokeWidth="1" />
                  </g>
                )}

                {/* Direction arrow */}
                <polygon points="92,22 86,18 86,26" fill={isRunning ? color : "#94a3b8"} opacity="0.8" />

                {/* Premium status dot - top right */}
                <circle cx="92" cy="8" r="4.5" fill={dotColor} stroke="#ffffff" strokeWidth="1.5" filter={isRunning ? "url(#conveyerGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
                <text x="50" y="52" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
                  {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Belt track */}
                <rect x="5" y="12" width="90" height="20" rx="10" fill={`${color}10`} stroke={color} strokeWidth="2" />

                {/* Belt surface with moving pattern */}
                <g style={{ animation: isRunning ? `live-conveyer-move ${beltDuration} linear infinite` : "none" }}>
                  {[15, 30, 45, 60, 75].map((x) => (
                    <line key={x} x1={x} y1="14" x2={x} y2="30" stroke={color} strokeWidth="1" opacity="0.3" />
                  ))}
                </g>

                {/* Rollers */}
                <circle cx="15" cy="22" r="6" fill={`${color}15`} stroke={color} strokeWidth="1.5"
                  style={{ transformOrigin: "15px 22px", animation: isRunning ? `live-pump-spin ${rollerDuration} linear infinite` : "none" }} />
                <circle cx="85" cy="22" r="6" fill={`${color}15`} stroke={color} strokeWidth="1.5"
                  style={{ transformOrigin: "85px 22px", animation: isRunning ? `live-pump-spin ${rollerDuration} linear infinite` : "none" }} />

                {/* Material on belt */}
                {isRunning && (
                  <g style={{ animation: `live-conveyer-move ${3 / (speedScale || 1)}s linear infinite` }}>
                    <rect x="30" y="8" width="8" height="6" rx="1" fill={`${color}50`} />
                    <rect x="55" y="8" width="8" height="6" rx="1" fill={`${color}50`} />
                  </g>
                )}

                {/* Direction arrow */}
                <polygon points="92,22 86,18 86,26" fill={isRunning ? color : "#94a3b8"} opacity="0.6" />

                {/* Status - top right */}
                <circle cx="92" cy="8" r="4" fill={dotColor} />
                <text x="50" y="52" textAnchor="middle" fontSize="7" fontWeight="600" fill={dotColor}>
                  {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

ConveyerNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default ConveyerNode;
